"use client"

import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Zap, Crown, Star, CheckCircle, MessageSquare, History, UserPlus, LogIn } from "lucide-react"
import { useAuth } from "./auth/auth-context"
import { Badge } from "@/components/ui/badge"

type UpgradeScreenProps = {
  setActiveScreen: (screen: string, plan?: "monthly" | "yearly") => void
}

export function UpgradeScreen({ setActiveScreen }: UpgradeScreenProps) {
  const { user, isAnonymous } = useAuth()

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 },
  }

  const handleUpgradeClick = (plan: "monthly" | "yearly") => {
    if (isAnonymous) {
      setActiveScreen("signup")
    } else {
      localStorage.setItem("billingPlan", plan)
      setActiveScreen("payment", plan)
    }
  }

  const premiumFeatures = [
    {
      icon: <MessageSquare className="h-4 w-4" />,
      title: "Unlimited Scans",
      description: "Analyze as many messages as you want",
    },
    {
      icon: <Star className="h-4 w-4" />,
      title: "All Tone Modes",
      description: "Access all communication styles",
    },
    {
      icon: <Zap className="h-4 w-4" />,
      title: "AI Rewrites",
      description: "Get perfect message suggestions",
    },
    {
      icon: <History className="h-4 w-4" />,
      title: "Message History",
      description: "Save and access your history",
    },
  ]

  return (
    <div className="flex flex-col h-full p-5 pt-8">
      <div className="flex items-center mb-3">
        <Button
          variant="ghost"
          size="icon"
          className="mr-2 text-zinc-400 hover:text-white hover:bg-white/10"
          onClick={() => setActiveScreen("analyze")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-light">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-yellow-400 font-medium">
            Premium
          </span>{" "}
          Features
        </h1>
      </div>

      {/* Account status banner for non-logged in users */}
      {isAnonymous && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-xl p-3 mb-3 border border-amber-500/30"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UserPlus className="h-4 w-4 text-amber-400 mr-2" />
              <p className="text-sm">Create an account to get premium features</p>
            </div>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-white/10 hover:text-white"
                onClick={() => setActiveScreen("login")}
              >
                <LogIn className="h-3 w-3 mr-1" />
                Login
              </Button>
              <Button
                size="sm"
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:opacity-90"
                onClick={() => setActiveScreen("signup")}
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Sign Up
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 gap-4">
          {/* Hero section with crown */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-center py-2"
          >
            <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full p-3">
              <Crown className="h-8 w-8 text-amber-400" />
            </div>
          </motion.div>

          {/* Pricing cards in horizontal layout */}
          <div className="grid grid-cols-2 gap-3">
            {/* Monthly plan */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="gradient-border-amber glass-card rounded-xl p-3 flex flex-col"
            >
              <div className="mb-1">
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 mb-1">Monthly</Badge>
                <div className="text-2xl font-light">
                  $6.99
                  <span className="text-sm font-light text-zinc-400">/mo</span>
                </div>
              </div>
              <Button
                className="mt-auto bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:opacity-90 font-light rounded-xl"
                onClick={() => handleUpgradeClick("monthly")}
                disabled={user?.plan === "premium"}
                size="sm"
              >
                Choose
              </Button>
            </motion.div>

            {/* Yearly plan */}
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="gradient-border-amber glass-card rounded-xl p-3 flex flex-col relative overflow-hidden"
            >
              <div className="mb-1">
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 mb-1">Yearly</Badge>
                <div className="flex items-baseline">
                  <span className="text-sm text-zinc-400 line-through mr-2">$83.88</span>
                  <span className="text-2xl font-light">
                    $67.10
                    <span className="text-sm font-light text-zinc-400">/yr</span>
                  </span>
                </div>
                <div className="text-xs text-amber-400">$5.59/month · Save 20%</div>
              </div>
              <Button
                className="mt-auto bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:opacity-90 font-light rounded-xl"
                onClick={() => handleUpgradeClick("yearly")}
                disabled={user?.plan === "premium"}
                size="sm"
              >
                Best Value
              </Button>
            </motion.div>
          </div>

          {/* Features grid */}
          <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 gap-3 mt-1">
            {premiumFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={item}
                className="glass-card rounded-xl p-3 flex flex-col"
                whileHover={{ y: -3 }}
              >
                <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full h-8 w-8 flex items-center justify-center mb-2">
                  {feature.icon}
                </div>
                <h3 className="text-sm font-medium mb-1">{feature.title}</h3>
                <p className="text-xs text-zinc-400">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Comparison table */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-xl p-3 mt-1"
          >
            <h3 className="text-sm font-medium mb-2 flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-amber-400" />
              Free vs Premium
            </h3>

            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-zinc-400">Feature</div>
              <div className="text-zinc-400 text-center">Free</div>
              <div className="text-amber-400 text-center">Premium</div>

              <div className="border-t border-white/5 pt-1 col-span-3"></div>

              <div>Message scans</div>
              <div className="text-center">3 per week</div>
              <div className="text-center text-amber-400">Unlimited</div>

              <div>Tone modes</div>
              <div className="text-center">Casual only</div>
              <div className="text-center text-amber-400">All modes</div>

              <div>Message history</div>
              <div className="text-center">None</div>
              <div className="text-center text-amber-400">Unlimited</div>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-2"
          >
            <Button
              className="w-full gradient-bg text-white hover:opacity-90 h-12 text-lg rounded-xl font-light"
              onClick={() => handleUpgradeClick("yearly")}
              disabled={user?.plan === "premium"}
            >
              {isAnonymous ? (
                <>
                  <UserPlus className="mr-2 h-5 w-5" />
                  Sign Up & Upgrade
                </>
              ) : user?.plan === "premium" ? (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Already Premium
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-5 w-5" />
                  Upgrade Now
                </>
              )}
            </Button>

            <div className="text-center mt-2 text-xs text-zinc-500 font-light">
              Cancel anytime. No commitment required.
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

